import {
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { TargetNodeTypes } from "@p0security/shared/types/assessment/data/index";
import type { TargetNodeType } from "@p0security/shared/types/assessment/data/index";
import { FindingState } from "@p0security/shared/types/assessment/finding";
import {
  Badge,
  Button,
  Checkbox,
  Select,
  Spacing,
  Tooltip,
} from "@p0security/ui";
import type { CheckboxChangeEvent } from "@p0security/ui";
import { SearchInput } from "components/antd";
import { startCase } from "lodash";
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { FindingsContext } from "../contexts/FindingsContext";
import { FindingsExport } from "./FindingsExport";
import { ScopeSelect } from "./ScopeSelect";
import { targetNodeTypeIconFor } from "./cells/MonitorTypeIcon";
import { P0Badge } from "./cells/P0Badge";
import {
  FindingsIconLabel,
  UnassignedLabel,
  findingStateLabelFor,
} from "./finding/labels";

const stateOptions = FindingState.map((s) => ({
  label: findingStateLabelFor(s),
  value: s,
}));

const typeOptionLabelFor = (type: TargetNodeType) => (
  <FindingsIconLabel icon={targetNodeTypeIconFor(type)}>
    {startCase(type)}
  </FindingsIconLabel>
);

const sourceOptions = [
  { label: "All Monitors", value: "all" },
  {
    label: (
      <FindingsIconLabel icon={<EditOutlined />}>Custom</FindingsIconLabel>
    ),
    value: "custom",
  },
  {
    label: (
      <FindingsIconLabel
        icon={<P0Badge className="h-3 w-4 shrink-0 opacity-75" />}
      >
        Preset
      </FindingsIconLabel>
    ),
    value: "preset",
  },
];

export const FindingsFilterBar: React.FC = () => {
  const {
    state,
    setState,
    assignment,
    setAssignment,
    filterParams,
    setFilterParams,
    monitorFilter,
    setMonitorFilter,
    allMonitors,
  } = useContext(FindingsContext);
  const {
    type: typeFilter,
    fix: hasFixFilter,
    source: sourceFilter,
  } = filterParams;

  const [expanded, setExpanded] = useState(
    () =>
      state !== "open" ||
      assignment !== "all" ||
      typeFilter.length > 0 ||
      hasFixFilter ||
      sourceFilter !== "all"
  );

  const typeOptions = useMemo(() => {
    const typesWithMonitors = new Set<TargetNodeType>();
    for (const monitor of Object.values(allMonitors)) {
      if (TargetNodeTypes.includes(monitor.show)) {
        typesWithMonitors.add(monitor.show);
      }
    }
    return TargetNodeTypes.filter((t) => typesWithMonitors.has(t)).map((t) => ({
      label: typeOptionLabelFor(t),
      value: t,
    }));
  }, [allMonitors]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (state !== "open") count++;
    if (assignment !== "all") count++;
    if (typeFilter.length > 0) count++;
    if (hasFixFilter) count++;
    if (sourceFilter !== "all") count++;
    return count;
  }, [state, assignment, typeFilter, hasFixFilter, sourceFilter]);

  const onSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setMonitorFilter(e.target.value);
    },
    [setMonitorFilter]
  );

  const onClearAll = useCallback(() => {
    setState("open");
    setAssignment("all");
    setFilterParams({ type: [], fix: false, source: "all" });
    setMonitorFilter("");
  }, [setState, setAssignment, setFilterParams, setMonitorFilter]);

  const toggleExpanded = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const onTypeChange = useCallback(
    (type: TargetNodeType[]) => setFilterParams({ type }),
    [setFilterParams]
  );
  const onSourceChange = useCallback(
    (source: "all" | "custom" | "preset") => setFilterParams({ source }),
    [setFilterParams]
  );
  const handleCheckboxChange = useCallback(
    (e: CheckboxChangeEvent) => setFilterParams({ fix: e.target.checked }),
    [setFilterParams]
  );
  const handleAssignmentCheckboxChange = useCallback(
    (e: CheckboxChangeEvent) =>
      setAssignment(e.target.checked ? "unassigned" : "all"),
    [setAssignment]
  );

  return (
    <>
      <Spacing asChild mb={16}>
        <div className="flex items-center gap-2">
          <div className="max-w-[600px] min-w-[200px] flex-1">
            <SearchInput
              placeholder="Search monitors..."
              value={monitorFilter}
              onChange={onSearch}
              allowClear
            />
          </div>
          <Tooltip title="Toggle filters">
            <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
              <Button
                aria-label="Toggle filters"
                icon={<FilterOutlined />}
                type={expanded ? "primary" : "default"}
                onClick={toggleExpanded}
              />
            </Badge>
          </Tooltip>
          {activeFilterCount > 0 && (
            <Tooltip title="Clear all filters">
              <Button
                aria-label="Clear all filters"
                icon={<CloseOutlined />}
                onClick={onClearAll}
                type="text"
              />
            </Tooltip>
          )}
          <FindingsExport />
        </div>
      </Spacing>
      {expanded && (
        <Spacing asChild mb={16}>
          <div className="flex items-center gap-2">
            <Select
              value={state}
              onChange={setState}
              options={stateOptions}
              className="min-w-[120px]"
              placeholder="Status"
            />
            <Select
              mode="multiple"
              value={typeFilter}
              onChange={onTypeChange}
              options={typeOptions}
              className="min-w-[200px]"
              placeholder="All types"
              allowClear
              maxTagCount="responsive"
            />
            <Select
              value={sourceFilter}
              onChange={onSourceChange}
              options={sourceOptions}
              className="min-w-[120px]"
              placeholder="All monitor sources"
            />
            <ScopeSelect includeAll />
            <Checkbox
              checked={assignment === "unassigned"}
              onChange={handleAssignmentCheckboxChange}
            >
              <UnassignedLabel />
            </Checkbox>
            <Checkbox checked={hasFixFilter} onChange={handleCheckboxChange}>
              <FindingsIconLabel icon={<ToolOutlined />}>
                Has fix
              </FindingsIconLabel>
            </Checkbox>
          </div>
        </Spacing>
      )}
    </>
  );
};
